/**
 * Cliente OIDC contra o Keycloak.
 *
 * O login acontece na tela da própria SPA: usuário e senha vão direto ao
 * endpoint de token do Keycloak (grant `password`), que continua sendo
 * quem valida a senha, aplica a proteção contra força bruta e emite o JWT.
 * A API não muda nada — recebe o mesmo token de antes.
 *
 * O fluxo por redirect (Authorization Code + PKCE, `/auth/callback`) é o
 * dos casos que não cabem no formulário: login com Google/Apple, "Esqueceu
 * a senha?" e "Alterar senha" — ver redirects.ts.
 *
 * Requer `directAccessGrantsEnabled: true` no client `centralone-spa`.
 */
import { ErrorResponse, UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { config } from '@/core/config';

export const CALLBACK_PATH = '/auth/callback';
export const LOGIN_PATH = '/login';

export const userManager = new UserManager({
  authority: `${config.keycloak.url}/realms/${config.keycloak.realm}`,
  client_id: config.keycloak.clientId,
  redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
  post_logout_redirect_uri: `${window.location.origin}${LOGIN_PATH}`,
  response_type: 'code',
  scope: 'openid profile email',
  // Renova com o refresh token antes do access token expirar (15 min no realm).
  automaticSilentRenew: true,
  // sessionStorage: o token morre com a aba. localStorage o exporia a
  // qualquer script de qualquer aba pelo tempo de vida da sessão SSO.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

/** O que a SPA guarda no `state` do OIDC para voltar à página de origem. */
export interface SigninState {
  returnTo?: string;
}

// Espelho síncrono do token. Existe por causa do `fetch(keepalive)` disparado
// ao fechar a aba (telemetria de progresso): ali não dá para esperar a Promise
// de `getUser()`.
let current: User | null = null;
userManager.events.addUserLoaded((u) => {
  current = u;
});
userManager.events.addUserUnloaded(() => {
  current = null;
});
void userManager.getUser().then((u) => {
  current = u;
});

export async function getAccessToken(): Promise<string | undefined> {
  const user = await userManager.getUser();
  return user && !user.expired ? user.access_token : undefined;
}

export function getAccessTokenSync(): string | undefined {
  return current && !current.expired ? current.access_token : undefined;
}

/** Caminho interno seguro para redirecionar — nunca uma URL de outro host. */
export function safeReturnTo(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return '/';
  if (value.startsWith(LOGIN_PATH) || value.startsWith(CALLBACK_PATH)) return '/';
  return value;
}

/** Login com usuário (ou e-mail) e senha, sem sair da SPA. */
export async function signInWithPassword(username: string, password: string): Promise<void> {
  // skipUserInfo: o perfil vem da API (/identity/me), não do Keycloak.
  await userManager.signinResourceOwnerCredentials({ username, password, skipUserInfo: true });
}

/**
 * Sai sem passar pela tela do Keycloak: revoga o refresh token, o que
 * encerra a sessão lá, e descarta os tokens locais.
 */
export async function signOut(): Promise<void> {
  try {
    await userManager.revokeTokens(['refresh_token']);
  } catch {
    // Keycloak fora do ar não pode prender o usuário logado na tela.
  }
  await userManager.removeUser();
}

/** Traduz a resposta do Keycloak para uma mensagem de tela. */
export function signInErrorMessage(error: unknown): string {
  if (error instanceof ErrorResponse) {
    const description = error.error_description ?? '';
    if (error.error === 'invalid_grant') {
      if (/disabled/i.test(description)) return 'Sua conta está desativada. Fale com o administrador.';
      if (/not fully set up/i.test(description))
        return 'Sua conta tem uma pendência de cadastro. Fale com o administrador.';
      // Senha errada e conta bloqueada por força bruta chegam iguais — de
      // propósito, para não revelar qual das duas aconteceu.
      return 'Usuário ou senha inválidos.';
    }
    if (error.error === 'unauthorized_client')
      return 'Login por senha não está habilitado no servidor de autenticação.';
    return 'Não foi possível entrar. Tente novamente.';
  }
  return 'Não foi possível conectar ao servidor de autenticação.';
}
